import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Avatar,
  Button,
  IconButton,
  Typography,
  TextField,
  Grid,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import imageService from '../services/image.service';

interface ThumbnailPickerProps {
  value: number | null;
  onChange: (imageId: number | null) => void;
  currentThumbnailUrl?: string | null;
}

const ThumbnailPicker: React.FC<ThumbnailPickerProps> = ({ value, onChange, currentThumbnailUrl }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [images, setImages] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const loadImages = useCallback(async (searchTerm: string) => {
    try {
      setLoading(true);
      const data = await imageService.getAllForPicker(searchTerm || undefined);
      setImages(data);
    } catch (e) {
      console.error('Error loading images for picker:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showPicker) {
      loadImages(search);
    }
  }, [showPicker]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showPicker) return;
    const timeout = setTimeout(() => {
      loadImages(search);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedImage = images.find(img => img.id === value);
  const displayUrl = selectedImage?.thumbnail_url || selectedImage?.image_file_url || currentThumbnailUrl;

  return (
    <Box mt={1} mb={1}>
      <Typography variant="body2" gutterBottom>Vignette</Typography>
      <Box display="flex" alignItems="center" gap={2}>
        {value ? (
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={displayUrl || undefined}
              variant="rounded"
              sx={{ width: 48, height: 48 }}
            />
            <IconButton
              size="small"
              onClick={() => onChange(null)}
              sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', boxShadow: 1 }}
            >
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ) : (
          <Avatar variant="rounded" sx={{ width: 48, height: 48, bgcolor: 'action.hover' }}>
            <FolderIcon />
          </Avatar>
        )}
        <Button variant="outlined" size="small" onClick={() => setShowPicker(!showPicker)}>
          {showPicker ? 'Masquer' : 'Choisir'}
        </Button>
      </Box>
      {showPicker && (
        <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Rechercher une image..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
            {loading ? (
              <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>Chargement...</Typography>
            ) : images.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>Aucune image trouvée</Typography>
            ) : (
              <Grid container spacing={0.5}>
                {images.map((img) => (
                  <Grid item key={img.id}>
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        src={img.thumbnail_url || img.image_file_url}
                        variant="rounded"
                        sx={{
                          width: 48,
                          height: 48,
                          cursor: 'pointer',
                          border: value === img.id ? '2px solid' : '2px solid transparent',
                          borderColor: value === img.id ? 'primary.main' : 'transparent',
                        }}
                        onClick={() => {
                          onChange(img.id);
                          setShowPicker(false);
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          textAlign: 'center',
                          maxWidth: 48,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.6rem',
                        }}
                      >
                        {img.title}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ThumbnailPicker;
